export type CefrBand = 'A2' | 'B1' | 'C1';
export type CefrSkill = 'reading' | 'writing' | 'listening' | 'speaking';

export type SkillQuestion = {
  id: CefrSkill;
  label: string;
  icon: string;
  prompt: string;
  options: Array<{ band: CefrBand; label: string }>;
};

export const CEFR_SKILLS: SkillQuestion[] = [
  {
    id: 'reading',
    label: 'Reading',
    icon: 'menu_book',
    prompt: 'When you read this language, which feels most true?',
    options: [
      {
        band: 'A2',
        label: 'I can understand short, simple texts and everyday notices.',
      },
      {
        band: 'B1',
        label: 'I can follow the main points in work emails, news, and job ads.',
      },
      {
        band: 'C1',
        label: 'I can read complex reports, contracts, and academic texts with ease.',
      },
    ],
  },
  {
    id: 'writing',
    label: 'Writing',
    icon: 'edit_note',
    prompt: 'When you write this language, which feels most true?',
    options: [
      {
        band: 'A2',
        label: 'I can write short notes, simple emails, and basic forms.',
      },
      {
        band: 'B1',
        label: 'I can write a CV, cover letter, and clear emails about work.',
      },
      {
        band: 'C1',
        label: 'I can write detailed reports and nuanced arguments with few errors.',
      },
    ],
  },
  {
    id: 'listening',
    label: 'Listening',
    icon: 'headphones',
    prompt: 'When you listen to this language, which feels most true?',
    options: [
      {
        band: 'A2',
        label: 'I catch slow, clear speech about familiar topics.',
      },
      {
        band: 'B1',
        label: 'I follow meetings and interviews if people speak reasonably clearly.',
      },
      {
        band: 'C1',
        label: 'I understand native-speed discussion, humour, and implied meaning.',
      },
    ],
  },
  {
    id: 'speaking',
    label: 'Speaking',
    icon: 'record_voice_over',
    prompt: 'When you speak this language, which feels most true?',
    options: [
      {
        band: 'A2',
        label: 'I can introduce myself and handle simple, rehearsed conversations.',
      },
      {
        band: 'B1',
        label: 'I can discuss my work, explain ideas, and handle most interviews.',
      },
      {
        band: 'C1',
        label: 'I can debate, persuade, and speak fluently with little searching for words.',
      },
    ],
  },
];

const BAND_RANK: Record<CefrBand, number> = { A2: 0, B1: 1, C1: 2 };

export function overallCefrLevel(levels: CefrBand[]): CefrBand {
  if (levels.length === 0) return 'A2';
  const min = Math.min(...levels.map((level) => BAND_RANK[level]));
  return (Object.keys(BAND_RANK) as CefrBand[]).find(
    (band) => BAND_RANK[band] === min,
  ) ?? 'A2';
}

export function formatEuropassLine(
  language: string,
  skills: Array<{ skill: string; level: CefrBand }>,
  overall: CefrBand,
): string {
  const parts = skills.map((item) => `${item.skill} ${item.level}`).join(', ');
  return `${language.trim()} — overall ${overall} (${parts})`;
}
